import { create } from 'zustand';
import type { Lesson, LessonVersion, LessonPlan, SlideMapping, SlidesHTML, SlideData } from '../types';

interface LessonState {
  currentLesson: Lesson | null;
  currentVersion: LessonVersion | null;
  setCurrentLesson: (lesson: Lesson | null) => void;
  setCurrentVersion: (version: LessonVersion | null) => void;
  updateLessonPlan: (plan: LessonPlan) => void;
  updateSlideMapping: (mapping: SlideMapping) => void;
  updateSlidesHTML: (slides: SlidesHTML) => void;
  patchSlideNotes: (stageName: string, slideNumber: number, notes: string) => void;
  reset: () => void;
}

const initialState = {
  currentLesson: null,
  currentVersion: null,
};

export const useLessonStore = create<LessonState>((set) => ({
  ...initialState,

  setCurrentLesson: (lesson) => set({ currentLesson: lesson }),

  setCurrentVersion: (version) => set({ currentVersion: version }),

  updateLessonPlan: (plan) =>
    set((state) => ({
      currentVersion: state.currentVersion
        ? { ...state.currentVersion, lesson_plan: plan }
        : null,
    })),

  updateSlideMapping: (mapping) =>
    set((state) => ({
      currentVersion: state.currentVersion
        ? { ...state.currentVersion, slide_mapping: mapping }
        : null,
    })),

  updateSlidesHTML: (slides) =>
    set((state) => ({
      currentVersion: state.currentVersion
        ? { ...state.currentVersion, slides_html: slides }
        : null,
    })),

  patchSlideNotes: (stageName, slideNumber, notes) =>
    set((state) => {
      if (!state.currentVersion?.slides_html) return state;
      const slidesHTML = state.currentVersion.slides_html as SlidesHTML & Record<string, unknown>;
      const stageSlides = slidesHTML[stageName];
      if (!Array.isArray(stageSlides)) return state;
      const updatedStage = (stageSlides as SlideData[]).map((slide) =>
        slide.slide_number === slideNumber
          ? { ...slide, speaker_notes: notes }
          : slide
      );
      return {
        currentVersion: {
          ...state.currentVersion,
          slides_html: { ...slidesHTML, [stageName]: updatedStage } as SlidesHTML,
        },
      };
    }),

  reset: () => set(initialState),
}));
